import { AxiosHeaders, AxiosResponse } from "axios";
import { Media } from "../types/fhir";
import { apiClientService } from "./apiClientService";
import { IExtentionType } from "../types/IQuestionnareItemType";

const apiGatewayInstance = apiClientService.apiGateway;

// CRUD operations for Media
//https://www.hl7.org/fhir/media.html

export enum MediaStatus {
  Unknown = 404,
  Deleted = 410,
  Active = 200,
}

export type MediaResponse = {
  data: Media | null;
  status: MediaStatus;
};

const getAll = async (): Promise<Media[] | undefined> => {
  const httpResponse = await apiGatewayInstance.get(`api/Media`);
  if (httpResponse.status !== 200) {
    throw new Error("An error occurred getting all Media");
  }
  return Array.isArray(httpResponse.data) ? (httpResponse.data as Media[]) : [];
};

const getAllForOverview = async (): Promise<Media[] | undefined> => {
  const httpResponse = await apiGatewayInstance.get(`api/Media/GetAllMediaForOverview`);
  if (httpResponse.status !== 200) {
    throw new Error("An error occurred getting Media for overview");
  }
  return Array.isArray(httpResponse.data) ? (httpResponse.data as Media[]) : [];
};

const getOneWithVersion = async (id: string, version: string): Promise<MediaResponse> => {
  const httpResponse = await apiGatewayInstance.get(`api/Media/${id}/_history/${version}`);
  if (httpResponse.status === 410) {
    return { data: null, status: MediaStatus.Deleted };
  }
  if (httpResponse.status === 404) {
    //Resource is not known
    return { data: null, status: MediaStatus.Unknown };
  }
  if (httpResponse.status !== 200) {
    return { data: null, status: MediaStatus.Unknown };
  }

  return { data: httpResponse.data as Media, status: MediaStatus.Active };
};

const getOne = async (id: string): Promise<MediaResponse> => {
  //If there is a version on, call the correct method
  if (id.includes("/")) {
    const splittedVersion = id.split("/");
    return await getOneWithVersion(splittedVersion[0], splittedVersion[1]);
  }

  const httpResponse = await apiGatewayInstance.get(`api/Media/${id}`);
  if (httpResponse.status === 410) {
    return { data: null, status: MediaStatus.Deleted };
  }
  if (httpResponse.status === 404) {
    //Resource is not known
    return { data: null, status: MediaStatus.Unknown };
  }
  if (httpResponse.status !== 200) {
    return { data: null, status: MediaStatus.Unknown };
  }

  return { data: httpResponse.data as Media, status: MediaStatus.Active };
};

const getByTitle = async (title: string): Promise<Media[] | undefined> => {
  const httpResponse = await apiGatewayInstance.get(`api/Media/GetMediaByTitle?title=${title}`);
  if (httpResponse.status !== 200) {
    throw new Error("An error occurred getting Media by title");
  }
  return Array.isArray(httpResponse.data) ? (httpResponse.data as Media[]) : [];
};

/**
 * @param media
 * Add a new media. If media with same hash already exists, it will return that media.
 * @returns 200 if existing media was returned or 201 if new media was created
 */
const addOne = async (media: Media): Promise<AxiosResponse<Media | null>> => {
  const sha1Hash = media?.extension?.find((ext) => ext.url === IExtentionType.sha1Hash)?.valueString;
  if (sha1Hash) {
    //Check if image already exists on server and return it
    const image = await getOneByHash(sha1Hash);
    if (image) {
      return { data: image, status: 200, statusText: "OK", headers: {}, config: { headers: new AxiosHeaders() } };
    }
  }

  const httpResponse = await apiGatewayInstance.post(`api/Media`, media);
  if (httpResponse.status !== 200 && httpResponse.status !== 201) {
    throw new Error("An error occurred creating Media");
  }

  return { data: httpResponse.data as Media, status: httpResponse.status, statusText: httpResponse.statusText, headers: httpResponse.headers, config: httpResponse.config };
};

const getOneByHash = async (hash: string): Promise<Media | undefined> => {
  const httpResponse = await apiGatewayInstance.get(`api/Media/GetAllMediaForHashLookup`);
  if (httpResponse.status !== 200) {
    return undefined;
  }

  const mediaList = Array.isArray(httpResponse.data) ? (httpResponse.data as Media[]) : [];
  const foundImage = mediaList.find(
    (media) => media?.extension?.find((ext) => ext.url === IExtentionType.sha1Hash)?.valueString === hash
  );
  if (foundImage) {
    const fullImage = await getOne(foundImage.id ?? "");
    return fullImage.data ?? foundImage;
  }
  return undefined;
};

const updateOne = async (media: Media): Promise<number> => {
  const response = await apiGatewayInstance.put(`api/Media/${media.id}`, media);
  return response.status;
};

const removeOne = async (id: string): Promise<number> => {
  const response = await apiGatewayInstance.delete(`api/Media/${id}`);
  return response.status;
};

const mediaService = {
  getAll,
  getOne,
  getOneWithVersion,
  getOneByHash,
  getByTitle,
  addOne,
  updateOne,
  removeOne,
  getAllForOverview,
};

export default mediaService;
